import { type MaturityByProductGroup, type MaturityModel, type ProductMaturity } from '@/domain/maturityModel/types';
import { type ProductRelatedPlant } from '@/domain/plant/types';
import api, { buildURLWithParams } from '@/services/api';
import { handleException } from '@/utils/exception';
import { toast } from 'react-toastify';

import {
  type ExportableSolution,
  type GetProductsSimpleQuery,
  type ImportAIZipProductDraftResponse,
  type Product,
  type ProductBasic,
  type ProductBrief,
  type ProductDocumentTypeDTO,
  type ProductDraft,
  type ProductImportResult,
  type ProductList,
  type ProductMaturityHistoryGroup,
  type ProductRoadmap,
  type ProductSimple,
  type ProductSitemap,
  type ProductUpdate,
  type SaveProductDraftDTO,
} from '../types';

const BASE_URL = 'products';
const DRAFT_BASE_URL = 'ProductDraft';

export const getProductsSimple = async ({
  showProducts,
  showAddons,
  industry,
  productGroup,
}: GetProductsSimpleQuery) => {
  return await api.get<ProductSimple[]>(
    buildURLWithParams(`${BASE_URL}/simple`, { showProducts, showAddons, industry, productGroup }),
  );
};

export const getMaturityModel = async (productId: number, maturityModelId: number) => {
  try {
    return await api.get<MaturityModel>(`${BASE_URL}/${productId}/maturity/${maturityModelId}`);
  } catch (e) {
    toast.error(handleException(e, 'Failed to get maturity model'));
    throw e;
  }
};

export const getProductMaturityModels = async () => {
  try {
    return await api.get<ProductMaturity[]>(`${BASE_URL}/maturity-models`);
  } catch (e) {
    toast.error(handleException(e, 'Failed to get maturity model'));
    throw e;
  }
};

export const getMaturityReportByGroup = async (maturityId: number) => {
  try {
    return await api.get<MaturityByProductGroup>(`${BASE_URL}/maturity-models/${maturityId}/report/group`);
  } catch (e) {
    toast.error(handleException(e, 'Failed to get maturity model'));
    throw e;
  }
};

export const getMaturityReportByOptions = async (maturityId: number) => {
  try {
    return await api.get<MaturityByProductGroup>(`${BASE_URL}/maturity-models/${maturityId}/report/options`);
  } catch (e) {
    toast.error(handleException(e, 'Failed to get maturity model'));
    throw e;
  }
};

export const getProductMaturity = async (maturityId: number) => {
  try {
    return await api.get<MaturityModel>(`${BASE_URL}/maturity/${maturityId}`);
  } catch (e) {
    toast.error(handleException(e, 'Failed to get maturity model'));
    throw e;
  }
};

export const getProductRoadMaps = async (id: number) => {
  return await api.get<ProductRoadmap[]>(`${BASE_URL}/${id}/roadmaps`);
};

export const getProductDocumentType = async () => {
  return await api.get<ProductDocumentTypeDTO[]>(`${BASE_URL}/document-type`);
};

export const approveReleasePo = async (id: number, status: boolean) => {
  await api.patch(`${BASE_URL}/${id}/release/po`, { status });
};

export const approveReleasePm = async (id: number, status: boolean) => {
  await api.patch(`${BASE_URL}/${id}/release/pm`, { status });
};

export const getMaturityHistory = async (id: number) => {
  return await api.get<ProductMaturityHistoryGroup[]>(`${BASE_URL}/${id}/maturity/history`);
};

export const updateProductFromDraft = async (productDraft: ProductDraft) => {
  return await api.put<ProductBasic>(BASE_URL, productDraft);
};

/**
 * A save that carries the images picked while editing: the JSON goes in `payload`, the files in
 * `images`. The payload references each file by position with a negative asset id, where -1 is the
 * first file, and the server swaps those for the ids of the rows it inserts.
 */
const buildSaveFormData = (payload: unknown, images: File[]) => {
  const formData = new FormData();
  formData.append('payload', JSON.stringify(payload));
  images.forEach((image) => formData.append('images', image));
  return formData;
};

export const createProduct = async (newProduct: ProductUpdate, images: File[] = []) => {
  return await api.post<ProductBasic>(BASE_URL, buildSaveFormData(newProduct, images));
};

export const getProductDraft = async (productId: number) => {
  return await api.get<ProductDraft>(`${DRAFT_BASE_URL}/product/${productId}`);
};

export const deleteProductDraft = async (draftId: number) => {
  return await api.delete(`${DRAFT_BASE_URL}/${draftId}`);
};
export const saveProductDraft = async (dto: SaveProductDraftDTO, draftId?: number, images: File[] = []) => {
  const url = draftId ? `${DRAFT_BASE_URL}?draftId=${draftId}` : DRAFT_BASE_URL;
  return await api.post<ProductDraft>(url, buildSaveFormData(dto, images));
};

export const publishProductDraft = async (draftId: number, rowVersion: string) => {
  return await api.post<ProductBasic>(`${DRAFT_BASE_URL}/${draftId}/publish`, { rowVersion });
};

export const importAIZipProductDraft = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return await api.post<ImportAIZipProductDraftResponse>(`${DRAFT_BASE_URL}/import-AI`, formData);
};

export const approveImportAIProductDraft = async (importToken: string) => {
  return await api.post<boolean>(`${DRAFT_BASE_URL}/import-AI/approve`, importToken);
};

export const notifyImportAIProductDraft = async () => {
  return await api.post(`${DRAFT_BASE_URL}/import-AI/notify`);
};

export const createProductBatch = async (product: ProductUpdate[]) => {
  return await api.post<ProductImportResult>(`${BASE_URL}/batch`, product);
};

export const getProducts = async () => {
  return await api.get<Product[]>(BASE_URL);
};

export const getProductById = async (id: number, filterRelatedProducts = true) => {
  return await api.get<Product>(`products/${id}?filterRelatedProducts=${filterRelatedProducts}`);
};

export const getProductDetailById = async (id: number) => {
  return await api.get<Product>(`products/${id}/detail`);
};

export const getProductDetailPublicById = async (id: number) => {
  return await api.get<Product>(`products/${id}/detail/public`);
};

export const getProductSimpleById = async (id: number) => {
  return await api.get<ProductSimple>(`products/${id}/simple`);
};

export const getProductListById = async (id: number) => {
  return await api.get<ProductList>(`products/${id}/list`);
};

export const getProductsBrief = async () => {
  return await api.get<ProductBrief[]>('products/brief');
};

export const getProductsBasic = async () => {
  return await api.get<ProductBasic[]>('products/basic');
};

export const getProductSitemap = async () => {
  return await api.get<ProductSitemap[]>('products/sitemap');
};

export const getProductsList = async () => {
  return await api.get<ProductList[]>('products/list');
};

export const deleteProduct = async (id: number) => {
  await api.delete(`products/${id}`);
};

export const addProductBenefit = async (id: number, ids: number[]) => {
  const data = { ids };
  await api.put(`products/${id}/benefit`, data);
};

export const getProductPlants = async (id: number) => {
  return await api.get<ProductRelatedPlant[]>(`products/${id}/plants`);
};

export const getExportableProducts = async (all = false) => {
  return await api.get<ExportableSolution[]>(buildURLWithParams(`${BASE_URL}/exportable`, { all }));
};
